import { useI18n } from '../../i18n/I18nProvider';
import { computeTrialHealth, pendingCount } from './dashboard.api';
import type { DashboardData, RecentDocument } from './dashboard.api';
import './dashboard.css';

const DOCUMENT_STATUS_KEYS: Record<RecentDocument['status'], string> = {
  draft: 'dashboard.statusDraft',
  pending: 'dashboard.statusPending',
  approved: 'dashboard.statusApproved',
  posted: 'dashboard.statusPosted',
  cancelled: 'dashboard.statusCancelled',
};

const PERIOD_STATUS_KEYS: Record<string, string> = {
  open: 'dashboard.statusOpen',
  closing: 'dashboard.statusClosing',
  closed: 'dashboard.statusClosed',
};

export function DashboardView({ data }: { data: DashboardData }): JSX.Element {
  const { t } = useI18n();
  const health = computeTrialHealth(data.trial.totals);
  const pending = pendingCount(data.documents);
  const isEmpty = data.periods.length === 0 && data.documents.length === 0;

  return (
    <div className="dash">
      <header className="page-head">
        <div>
          <h1>{t('dashboard.title')}</h1>
          <p className="muted">{t('dashboard.subtitle')}</p>
        </div>
      </header>

      <div className="dash__metrics">
        <article className="metric" data-testid="metric-turnover">
          <span className="metric__label">{t('dashboard.turnover')}</span>
          <span className="metric__value">{health.turnover}</span>
          <span className="muted">{t('dashboard.currency')}</span>
        </article>
        <article className="metric" data-testid="metric-debit">
          <span className="metric__label">{t('dashboard.debitBalance')}</span>
          <span className="metric__value">{health.debit}</span>
          <span className="muted">{t('dashboard.closingBalance')}</span>
        </article>
        <article className="metric" data-testid="metric-credit">
          <span className="metric__label">{t('dashboard.creditBalance')}</span>
          <span className="metric__value">{health.credit}</span>
          <span className="muted">{t('dashboard.closingBalance')}</span>
        </article>
        <article className="metric" data-testid="metric-pending">
          <span className="metric__label">{t('dashboard.pendingDocuments')}</span>
          <span className="metric__value">{String(pending)}</span>
          <span className="muted">{t('dashboard.pendingHint')}</span>
        </article>
      </div>

      <div className="dash__grid">
        <section className="card" data-testid="balance-health" data-balanced={health.balanced ? 'true' : 'false'}>
          <div className="dash__card-head">
            <h2>{t('dashboard.balanceHealth')}</h2>
            <span className={health.balanced ? 'pill pill--ok' : 'pill pill--warn'}>
              {health.balanced ? t('dashboard.balanced') : t('dashboard.unbalanced')}
            </span>
          </div>
          <dl className="dash__health">
            <div>
              <dt>{t('dashboard.debitEqualsCredit')}</dt>
              <dd>
                {t('dashboard.turnover')}: {health.turnover} {t('dashboard.currency')}
              </dd>
            </div>
            <div>
              <dt>{t('dashboard.difference')}</dt>
              <dd data-testid="balance-difference">{health.difference}</dd>
            </div>
          </dl>
        </section>

        <section className="card" data-testid="open-periods">
          <div className="dash__card-head">
            <h2>{t('dashboard.openPeriods')}</h2>
          </div>
          {data.periods.length === 0 ? (
            <p className="muted">{t('dashboard.noPeriods')}</p>
          ) : (
            <ul className="dash__periods">
              {data.periods.map((period) => (
                <li key={period.id} data-testid="period-row" data-status={period.status}>
                  <strong>{`${String(period.month).padStart(2, '0')}.${String(period.year)}`}</strong>
                  <span className="pill">{t(PERIOD_STATUS_KEYS[period.status] ?? 'dashboard.statusOpen')}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <section className="card">
        <div className="dash__card-head">
          <h2>{t('dashboard.recentDocuments')}</h2>
        </div>
        {isEmpty || data.documents.length === 0 ? (
          <p className="muted" data-testid="dashboard-empty">
            {t('dashboard.empty')}
          </p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>{t('dashboard.number')}</th>
                <th>{t('dashboard.type')}</th>
                <th>{t('dashboard.date')}</th>
                <th>{t('dashboard.status')}</th>
                <th>{t('dashboard.author')}</th>
              </tr>
            </thead>
            <tbody>
              {data.documents.map((document) => (
                <tr key={document.id} data-testid="recent-document" data-status={document.status}>
                  <td className="mono">{document.number}</td>
                  <td>{document.type}</td>
                  <td>{document.date}</td>
                  <td>
                    <span className="pill">{t(DOCUMENT_STATUS_KEYS[document.status])}</span>
                  </td>
                  <td>{document.author}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
